import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGoBack } from '../../hooks/useGoBack'
import { formatEventPeriod } from '../../lib/eventPeriod'
import { onAuthStateChanged } from 'firebase/auth'
import { CalendarDays, ChevronDown, ChevronRight, ChevronUp, ExternalLink, FlaskConical, MapPin, QrCode, Sparkles, Stamp, Store as StoreIcon, Ticket, ZoomIn } from 'lucide-react'
import StampProgramInfoButton from '../../components/StampProgramInfoButton'
import { auth } from '../../lib/firebase'
import ActivityCard from '../../components/ActivityCard'
import GuestUpgradeCard from '../../components/GuestUpgradeCard'
import Header from '../../components/Header'
import ImageLightbox from '../../components/ImageLightbox'
import PrizeStockBadge from '../../components/PrizeStockBadge'
import StampProgress from '../../components/StampProgress'
import StampProgramPanel from '../../components/StampProgramPanel'
import StampRegionPicker from '../../components/StampRegionPicker'
import { useGeolocation } from '../../hooks/useGeolocation'
import { useI18n } from '../../i18n/useI18n'
import type {
  StampEventGroup,
  StampQrProgramBlock,
  StampSlot,
  StampUpcomingBlock,
} from '../../services/api'
import { getGuestId } from '../../lib/guestId'
import { useStore } from '../../store/useStore'
import LotteryResultCard from '../../components/LotteryResultCard'
import LotteryDrawReveal from '../../components/LotteryDrawReveal'
import LotteryKeywordCard from '../../components/LotteryKeywordCard'
import { useStampRally } from '../../store/useStampRally'
import { ProgramTitle } from './eventInfoParts'
import { SlotBottomBar, SlotPopup } from './SlotOverlays'
import SlotPrLabel from './SlotPrLabel'
import { isExternalSlotLink, recordSlotImpression, useOpenSlot } from './slotOpen'

/**
 * イベント枠（バナー / 広告）。
 *
 * 飛び先の開き方はお知らせ枠 (AnnouncementPopup) と揃える。以前は常に
 * target="_blank" だったため、`/stores/xxx` のようなアプリ内パスを設定すると
 * **PWA を出て別タブでアプリが開き直っていた**。
 *
 * type='ad' は掲載料を受け取っている枠なので PR 表記を必ず出す
 * （景品表示法のステマ規制。お知らせ枠の sponsored と同じ扱い）。
 */
function SlotBanner({
  slot,
  active = true,
  track,
}: {
  slot: StampSlot
  active?: boolean
  /** 計測を送るか。リハーサル中 (運営の確認) は false (slotOpen.ts) */
  track: boolean
}) {
  const { t } = useI18n()
  // #1886 飛び先が無い枠は**見せるための画像**。カード幅に収めると焼き込まれた
  // 文字が読めないので、タップで全画面に開けるようにする (開き方は slotOpen.ts で共通)。
  const { open: openSlot, viewing, closeViewing } = useOpenSlot(track)
  const external = isExternalSlotLink(slot.linkUrl)

  // #1997 出稿元に「何回出て何回押されたか」を返せるようにする。
  const rootRef = useRef<HTMLDivElement | null>(null)
  const countedRef = useRef(false)
  // **画像の結果が出るまでは「見た」に数えない。** 読み込み中の枠は帯だけの高さで、
  // その状態でも画面には入ってしまう。イベントのカードを開いた瞬間、まだ何も
  // 見えていない下の方の枠まで数えていた (この spec が捕まえた)。
  //
  // 失敗したときも数える側に倒す。**押せる状態で置かれている以上「出ている」**
  // ためで、ここを数えないと「表示 0 回 / クリック 5 回」という行が運営画面に出る。
  const [imageSettled, setImageSettled] = useState(false)

  /** 表示回数は 1 枠につき 1 回だけ。 */
  const countImpression = () => {
    if (countedRef.current) return
    countedRef.current = true
    recordSlotImpression(slot, track)
  }

  useEffect(() => {
    // **1 枠につき 1 回だけ数える。** カルーセルは 6 秒ごとに回るので、表に出る
    // たびに数えると**画面を開きっぱなしにしただけで表示回数が延々と増える**。
    // それでは枠どうしを比べられない。
    if (!active || !imageSettled || countedRef.current) return
    const el = rootRef.current
    if (!el) return
    // **実際に目に入ったときに数える。** 下にスクロールされずに終わった枠や、
    // カルーセルで画面外にいる枠を数えると、クリック率が実態よりかなり低く出る。
    const observer = new IntersectionObserver(
      (entries) => {
        if (countedRef.current || !entries.some((e) => e.isIntersecting)) return
        countImpression()
        observer.disconnect()
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, imageSettled, slot.id])

  const open = () => {
    // **押されたなら見られている。** 半分未満しか見えていない状態で押された場合や、
    // 画像の結果を待っている最中に押された場合に、ここで先に表示回数を立てておく。
    // これが無いとクリック率が 100% を超えることがあり、数字ごと疑われる。
    countImpression()
    // 飛び先の有無にかかわらず数える。**「押した」という関心の表明は同じ**なので、
    // 枠の作りで数え方を変えると枠どうしを比べられなくなる。
    openSlot(slot)
  }

  return (
    <div ref={rootRef} className="relative w-full overflow-hidden rounded-2xl bg-white shadow-card">
      <button onClick={open} className="block w-full text-left tap-effect">
        <img
          src={slot.imageUrl}
          alt=""
          className="w-full object-cover"
          onLoad={() => setImageSettled(true)}
          onError={() => setImageSettled(true)}
        />
        {/* 画像だけだと**押せることにも、押すと何が起きるかにも気付けない** — ポスターは
            眺めるものに見える。下に帯を付けて、行き先 (外部サイト / アプリ内) か拡大かを
            文字とアイコンで出す */}
        <span className="flex w-full items-center justify-between gap-2 border-t border-navy/10 px-4 py-2.5 font-body text-sm text-navy">
          {slot.linkUrl ? t('stampRally.slotOpenLink') : t('stampRally.slotZoom')}
          {!slot.linkUrl ? (
            <ZoomIn size={16} className="shrink-0 text-text-tertiary" />
          ) : external ? (
            <ExternalLink size={16} className="shrink-0 text-text-tertiary" />
          ) : (
            <ChevronRight size={16} className="shrink-0 text-text-tertiary" />
          )}
        </span>
      </button>
      {viewing && <ImageLightbox src={viewing} onClose={closeViewing} />}
      {/* 掲載料を受け取っている枠は PR 表記が必須 (SlotPrLabel)。 */}
      {slot.type === 'ad' && <SlotPrLabel />}
    </div>
  )
}

/**
 * #1924 リハーサル表示中であることを画面に出す帯。
 *
 * リハーサル (公開前のイベントを運営が通しで確かめる機能) は**会期を見ない** —
 * 本番の会期は数か月先のことがあり、会期で弾くと確認のたびに本番の日付を書き換える
 * 羽目になるため (stamp_service.is_event_live)。そのため 9/12 に 10/10 のイベントを
 * 開いても「開催中」の枠に入る。**仕様どおりだが、説明が無いとバグに見える。**
 *
 * 「開催中」のバッジをそのまま出すと本物と区別が付かないので、カードは
 * `rehearsal` で枠と文字色を変え、その上でこの帯に理由を書く。
 */
function RehearsalNotice() {
  const { t } = useI18n()
  return (
    <div className="mx-4 mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4">
      <p className="flex items-center gap-1.5 font-body text-sm font-bold text-amber-900">
        <FlaskConical size={16} className="shrink-0" />
        {t('stampRally.rehearsalNoticeTitle')}
      </p>
      <p className="mt-1 font-body text-xs leading-relaxed text-amber-900/80">
        {t('stampRally.rehearsalNoticeBody')}
      </p>
    </div>
  )
}

/**
 * イベント画面のタブ。開催中・開催予定・終了が縦に続くだけだと区別しにくく、終了したものは
 * どこにも出ていなかった。
 */
type EventTab = 'open' | 'upcoming' | 'ended'
const EVENT_TABS: EventTab[] = ['open', 'upcoming', 'ended']
const TAB_LABEL: Record<EventTab, string> = {
  open: 'stampRally.tabOpen',
  upcoming: 'stampRally.tabUpcoming',
  ended: 'stampRally.tabEnded',
}
const TAB_EMPTY: Record<EventTab, string> = {
  open: 'stampRally.tabOpenEmpty',
  upcoming: 'stampRally.tabUpcomingEmpty',
  ended: 'stampRally.tabEndedEmpty',
}
/** 終了タブに出す期間。API (stamp_service.ENDED_LOOKBACK_DAYS) と同じ値 */
const ENDED_LOOKBACK_DAYS = 90

/**
 * #1898 イベント (親) の詳細への入口。**イベントのカードのいちばん下**に置く。
 *
 * 企画詳細の上に「イベント」へのボタンを置いていたが、自分で選んだ企画のページに
 * 関係の薄いものが見えて違和感があった。期間と場所はカードの見出しに出ているので、
 * 本文や公式サイトを見たい人が、企画を見終えたカードの最後から辿れれば足りる。
 * 企画の「企画の詳細をみる」(白いカード) と取り違えないよう、文字のリンクにする。
 */
function EventInfoLink({ eventId }: { eventId: string }) {
  const navigate = useNavigate()
  const { t } = useI18n()
  return (
    <button
      onClick={() => navigate(`/stamp-rally/event/${encodeURIComponent(eventId)}`)}
      className="tap-effect flex items-center justify-center gap-1 py-1 font-body text-sm font-medium text-navy"
    >
      {t('stampRally.eventInfoCta')}
      <ChevronRight size={16} />
    </button>
  )
}

/**
 * #1898 イベント (親) のカード。紺ヘッダに**イベント名と開催期間**、中に企画が並ぶ。
 *
 * 以前は企画ごとにカードが並び、同じフェスのスタンプラリーと AR宝探しが別々の
 * イベントに見えていた。イベントが主語で、スタンプラリーは企画の 1 つ。
 */
function EventCard({
  group,
  badge,
  rehearsal,
  open,
  onToggle,
  children,
}: {
  group: StampEventGroup
  badge: ReactNode
  /** #1924 リハーサル中のイベントは**本物と違う見た目**にする (下の RehearsalNotice 参照)。 */
  rehearsal?: boolean
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  const { t, locale } = useI18n()
  return (
    <div
      className={`rounded-2xl overflow-hidden shadow-card${
        rehearsal ? ' ring-2 ring-amber-400' : ''
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full bg-navy p-5 text-white text-left tap-effect"
        aria-expanded={open}
      >
        <div className="flex items-center justify-between mb-1">
          <span
            className={`flex items-center gap-1.5 text-xs font-body font-medium ${
              rehearsal ? 'text-amber-300' : 'text-white/80'
            }`}
          >
            {badge}
          </span>
          {open ? (
            <ChevronUp size={18} className="text-white/70" />
          ) : (
            <ChevronDown size={18} className="text-white/70" />
          )}
        </div>
        <h1 className="font-display text-xl mb-1">{group.name}</h1>
        {group.description && (
          <p className="text-xs font-body text-white/80 mb-2">{group.description}</p>
        )}
        {/* #1886 日付だけだと「10月10日 – 10月10日」になり、知りたい開催時刻が出ない。
            期間は所属の企画から決まる (#1898 親は持たない)。 */}
        <p className="flex items-center gap-1.5 text-xs font-body text-white/70">
          <CalendarDays size={14} />
          {t('stampRally.eventPeriod')}: {formatEventPeriod(group.startsAt, group.endsAt, locale)}
        </p>
        {/* 会場は開催期間のすぐ下。本文 (イベント詳細) を開かないと、どこでやるのか分からなかった */}
        {group.venue && (
          <p className="mt-1 flex items-center gap-1.5 text-xs font-body text-white/70">
            <MapPin size={14} />
            {t('stampRally.venue')}: {group.venue}
          </p>
        )}
      </button>
      {open && <div className="flex flex-col gap-4 bg-navy/5 p-4">{children}</div>}
    </div>
  )
}

/**
 * 開催中の QR ラリーの企画。中身は #1898 以前の QR ラリーのセクションと同じ。
 * 企画名・説明はイベントと同じなら繰り返さない — 1 イベント 1 企画 (移行で包んだ企画)
 * だと、紺ヘッダと同じ名前が 2 回並ぶ。
 */
function QrProgramSection({
  program,
  group,
  isGuest,
  isLoggedIn,
  focusEventId,
  open,
  onToggle,
}: {
  program: StampQrProgramBlock
  group: StampEventGroup
  isGuest: boolean
  isLoggedIn: boolean
  focusEventId: string | null
  open: boolean
  onToggle: () => void
}) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const applyKeywordResult = useStampRally((s) => s.applyKeywordResult)
  const { event } = program
  const redirectTo = focusEventId
    ? `/stamp-rally?event=${encodeURIComponent(focusEventId)}`
    : '/stamp-rally'
  // #2490 合言葉を入れに来た人は、ログインの後**この企画の枠を開いた状態で**戻す (閉じた枠を
  // 探して開き直させない)。デモ用の位置の指定 (?lat=&lng=) が付いていれば引き継ぐ
  const keywordRedirectTo = (() => {
    const params = new URLSearchParams({ event: event.id, open: 'program' })
    const current = new URLSearchParams(window.location.search)
    for (const key of ['lat', 'lng']) {
      const value = current.get(key)
      if (value) params.set(key, value)
    }
    return `/stamp-rally?${params.toString()}`
  })()
  // #2490 合言葉の要る抽選で、まだ引いていない。当たりやくじが尽きていれば出さない
  // (入れても引けないものを勧めない。在庫の欄に「抽選：終了」が出ている)
  const lottery = program.lottery
  const showKeyword =
    !!lottery?.keywordRequired && !program.myDraw && lottery.remaining > 0 && lottery.remainingDraws > 0
  return (
    <StampProgramPanel
      icon={<Stamp size={18} />}
      label={t('stampRally.programQr')}
      name={event.name}
      description={event.description !== group.description ? event.description : ''}
      summary={`${program.progress?.stampCount ?? 0}/${event.stampGoal}`}
      open={open}
      onToggle={onToggle}
    >

      {/* 進捗 (ログイン時のみサーバから返る。未ログインは 0 表示) */}
      <StampProgress count={program.progress?.stampCount ?? 0} goal={event.stampGoal} />

      {/* 在庫 */}
      {program.prize && <PrizeStockBadge prize={program.prize} lottery={program.lottery} />}
      {program.prize?.status === 'sold_out' && (
        <p className="text-xs font-body text-text-tertiary px-1 -mt-2">
          {t('stampRally.stockSoldOutNotice')}
        </p>
      )}

      {/* #1871 引いた後は自分の結果を出す (残数はカードの中)。
        * 当たりのコードは列に並んだ後に見せるので、その場限りにしない。 */}
      {/* #2490 合言葉を入れてその場で引いたときは、スキャン直後と同じ演出で見せる */}
      {program.myDraw &&
        (program.myDraw.justDrawn ? (
          <LotteryDrawReveal draw={program.myDraw} />
        ) : (
          <LotteryResultCard draw={program.myDraw} />
        ))}

      {/* #2490 LINE 公式アカウントの友だち追加 → 届いた合言葉で抽選に参加 */}
      {showKeyword && lottery && (
        <LotteryKeywordCard
          eventId={event.id}
          lottery={lottery}
          keywordEntered={program.keywordEntered}
          hasStamp={(program.progress?.stampCount ?? 0) > 0}
          isLoggedIn={isLoggedIn}
          isGuest={isGuest}
          redirectTo={keywordRedirectTo}
          onResult={(result) => applyKeywordResult(event.id, result)}
        />
      )}

      {/* #1870 ゲスト → アカウント。**抽選のある企画だけ**に出す
        * (見返りが無いのに登録を勧めるのは押し売り)。完走していれば
        * 「あと1タップで抽選に参加できます」と強く出す — 動機が
        * いちばん高い場面。 */}
      {isGuest && program.lottery && (
        <GuestUpgradeCard
          completed={(program.progress?.stampCount ?? 0) >= event.stampGoal}
          redirectTo={redirectTo}
        />
      )}

      {/* アクション */}
      <button
        onClick={() => navigate('/scan', { state: { purpose: 'stamp' } })}
        className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red text-white py-3.5 text-sm font-body font-medium tap-effect"
      >
        <QrCode size={18} />
        {t('stampRally.openScanner')}
      </button>
      <p className="text-center text-xs font-body text-text-tertiary -mt-2">
        {t('stampRally.scanCta')}
      </p>

      <button
        onClick={() => navigate('/stamp-rally/stores')}
        className="flex items-center justify-between bg-white rounded-2xl shadow-card px-4 py-4 tap-card"
      >
        <span className="flex items-center gap-3 font-body text-text-primary">
          <StoreIcon size={20} className="text-navy" />
          {t('stampRally.viewStores')}
          <span className="text-xs text-text-tertiary">({program.stores.length})</span>
        </span>
        <ChevronRight size={18} className="text-text-tertiary" />
      </button>

      {/* チケットは企画ごと。同じイベントに QR 企画が 2 つあっても取り違えないよう名指しする */}
      <button
        onClick={() => navigate(`/stamp-rally/ticket?eventId=${encodeURIComponent(event.id)}`)}
        className="flex items-center justify-between bg-white rounded-2xl shadow-card px-4 py-4 tap-card"
      >
        <span className="flex items-center gap-3 font-body text-text-primary">
          <Ticket size={20} className="text-gold" />
          {t('stampRally.myTicket')}
        </span>
        <ChevronRight size={18} className="text-text-tertiary" />
      </button>

      <StampProgramInfoButton programId={event.id} />
    </StampProgramPanel>
  )
}

/**
 * 開催予定の企画。**開催中と同じ形**で出し、参加の操作だけ止める。
 *
 * 名前と日付だけだと、開催前に見せる側がまだ何も PR できない (景品も掲示物も
 * 開催前から決まっている)。
 */
function UpcomingProgramSection({
  program,
  group,
  open,
  onToggle,
}: {
  program: StampUpcomingBlock
  group: StampEventGroup
  open: boolean
  onToggle: () => void
}) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { event } = program
  const isQr = program.kind === 'qr'
  const label = isQr
    ? t('stampRally.programQr')
    : event.activityLabel || (event.type === 'ar' ? t('stampRally.arTitle') : t('stampRally.programActivity'))
  const cta = isQr
    ? t('stampRally.openScanner')
    : event.ctaLabel ||
      (event.type === 'ar'
        ? t('stampRally.arLaunchCta')
        : event.linkOnly
          ? t('stampRally.linkActivityCta')
          : t('stampRally.activityLaunchCta'))

  return (
    <StampProgramPanel
      icon={isQr ? <Stamp size={18} /> : <Sparkles size={18} />}
      label={label}
      name={event.name}
      description={event.description !== group.description ? event.description : ''}
      tone={isQr ? 'navy' : 'gold'}
      open={open}
      onToggle={onToggle}
    >

      {/* 進捗の枠。0 個から始まることと、何個で完走かが先に分かる。
          進捗を記録しない企画 (リンクで遊ぶだけ) には出さない。 */}
      {!event.linkOnly && <StampProgress count={0} goal={event.stampGoal} />}

      {program.prize && <PrizeStockBadge prize={program.prize} lottery={program.lottery} />}

      {/* 開催前は押せない。**隠さずに出す** — 当日の動き方が先に分かる
          方が、告知を見た人にも、説明する側にも親切。 */}
      <button
        disabled
        className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red/40 text-white py-3.5 text-sm font-body font-medium cursor-not-allowed"
      >
        {isQr ? <QrCode size={18} /> : <Sparkles size={18} />}
        {cta}
      </button>
      <p className="text-center text-xs font-body text-text-tertiary -mt-2">
        {t('stampRally.upcomingHint')}
      </p>

      {/* 出店店舗。開催中と同じ一覧へ送る — 検索もブース番号も獲得状況も
          揃っていて、ブースを見るならこの画面がいちばん見やすい。
          開催前の企画は context に入らないので `?event=` で名指しする。 */}
      {isQr && program.storeCount > 0 && (
        <button
          onClick={() => navigate(`/stamp-rally/stores?event=${event.id}`)}
          className="flex items-center justify-between bg-white rounded-2xl shadow-card px-4 py-4 tap-card"
        >
          <span className="flex items-center gap-3 font-body text-text-primary">
            <StoreIcon size={20} className="text-navy" />
            {t('stampRally.viewStores')}
            <span className="text-xs text-text-tertiary">({program.storeCount})</span>
          </span>
          <ChevronRight size={18} className="text-text-tertiary" />
        </button>
      )}

      <StampProgramInfoButton programId={event.id} />
    </StampProgramPanel>
  )
}

/** 同じ位置に枠が複数あるときの切り替え間隔。広告として読める長さ。 */
const SLOT_ROTATE_MS = 6000
/** #1997 スワイプしてから、これだけの間に来た click は「送る操作の一部」とみなす。 */
const SWIPE_CLICK_GUARD_MS = 400

/**
 * 同じ位置 (上 / 下) のイベント枠。**複数あれば 1 つの場所で順番に切り替える。**
 *
 * PR 枠を縦に積むと、2 枚目以降はスクロールしないと見られず、ほとんど見られない。
 * 同じ場所で入れ替えれば、掲載できる枠の数を増やしても 1 枠あたりの見られ方が落ちにくい。
 *
 * - 並びはサーバの順 (優先度の高い順)
 * - 下の点で何枚目か分かる。点を押す / 左右にスワイプで手でも切り替えられ、
 *   **手で操作したら自動の切り替えは止める** (読んでいる途中で変わらないように)
 * - 端末で「視差効果を減らす」を選んでいる人には自動で動かさない
 * - **枠の高さは表示中の画像に合わせる** (縦長のポスターと横長のバナーが混ざる)。
 *   一番高い画像にそろえると、横長のバナーの回に上下へ大きな空きができて崩れて見えた。
 *   高さはゆっくり変えて、下の表示が急に跳ねないようにする
 */
function SlotCarousel({ slots, track }: { slots: StampSlot[]; track: boolean }) {
  const { t } = useI18n()
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  )
  const [touchX, setTouchX] = useState<number | null>(null)
  // #1997 スワイプでスライドを送った時刻。直後の click を無視するのに使う
  const swipedAt = useRef(0)
  const count = slots.length
  const current = count ? index % count : 0
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    if (count < 2 || paused) return
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), SLOT_ROTATE_MS)
    return () => window.clearInterval(id)
  }, [count, paused])

  // 表示中の枠の高さを追う。画像は読み込み後に高さが決まるので、サイズの変化も見る
  useEffect(() => {
    const el = slideRefs.current[current]
    if (!el) return
    const update = () => setHeight(el.offsetHeight)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [current, count])

  if (count === 0) return null
  if (count === 1) return <SlotBanner slot={slots[0]} track={track} />

  const goTo = (i: number) => {
    setIndex((i + count) % count)
    setPaused(true)
  }

  return (
    <div>
      <div
        className="overflow-hidden rounded-2xl transition-[height] duration-300 ease-out"
        style={height ? { height } : undefined}
        onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX === null) return
          const dx = e.changedTouches[0].clientX - touchX
          setTouchX(null)
          if (Math.abs(dx) > 40) {
            swipedAt.current = Date.now()
            goTo(current + (dx < 0 ? 1 : -1))
          }
        }}
        // #1997 **スワイプの直後の click を捨てる。** 指を滑らせてスライドを送ると、
        // 離した場所の枠で onClick も発火する。押したつもりが無いのにクリックとして
        // 数えられ、枠が複数あるイベントほどクリック率が水増しされる
        // (飛び先が外部サイトの枠だと、送ったつもりで別タブが開いてもいた)。
        onClickCapture={(e) => {
          if (Date.now() - swipedAt.current > SWIPE_CLICK_GUARD_MS) return
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <div
          className="flex items-start transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {slots.map((slot, i) => (
            <div
              key={slot.id}
              ref={(el) => {
                slideRefs.current[i] = el
              }}
              className="w-full shrink-0"
              aria-hidden={i !== current}
            >
              {/* #1997 表に出ている 1 枚だけを表示回数に数える。全部 DOM にあるので、
                  active を渡さないと**出していない枠まで数えてしまう** */}
              <SlotBanner slot={slot} active={i === current} track={track} />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2 flex justify-center gap-1.5">
        {slots.map((slot, i) => (
          <button
            key={slot.id}
            onClick={() => goTo(i)}
            aria-label={t('stampRally.slotGoTo', { n: String(i + 1), total: String(count) })}
            aria-current={i === current}
            className={`h-1.5 rounded-full transition-all ${i === current ? 'w-4 bg-navy' : 'w-1.5 bg-navy/25'}`}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * スタンプラリーTOP (#1126)。表示条件 = ジオフェンス内 (or 手動地域選択)
 * AND 企画が有効 AND 期間内。判定はサーバ (/stamp/context) が行い、
 * ここは reason に応じた状態表示に徹する。
 * デモ偽装: ?lat=35.3277&lng=139.3489 (useGeolocation の既存機構)。
 *
 * #1898 イベント (親) ごとのカードの中に企画が並ぶ。
 */
export default function StampRallyTop() {
  // #1361 戻るは履歴を1つ戻す（行き先を push すると履歴が積み上がる）
  const goBack = useGoBack('/home')
  const { t } = useI18n()
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const { status, coords, requestLocation } = useGeolocation()
  const {
    context, contextLoading, contextError, manualRegionId,
    loadContext, setManualRegion, refreshProgress, refreshActivityProgress,
  } = useStampRally()
  // #1870 イベントの入口 QR から来た場合は `?event=<id>`。**そのイベントだけを見せる。**
  // QR で特定のイベントに来た人に、地域選択をやり直させたり別のイベントを
  // 並べたりするのは、来た理由と噛み合わない。
  // #1898 ID は親でも企画でもよい (配布済みの QR は企画の ID を指していることがある)。
  const [searchParams] = useSearchParams()
  const focusEventId = searchParams.get('event')
  const [pickerOpen, setPickerOpen] = useState(false)
  // #1870 ゲストで参加している人にだけ、アカウントへの切り替えを出す。
  // **引き継ぎ (#1876) はログインすれば動くが、勧める場所が無ければ届かない。**
  const isGuest = !isLoggedIn && getGuestId() !== null
  // #1898 開閉。**既定は閉じる** — イベントのカードも企画の枠も見出しだけを並べ、押して開く。
  // 開いた状態で並べると、スタンプ枠・景品・ボタンが縦に続いて、何のイベントと企画があるのかを
  // 見渡せなかった。入口 QR (`?event=`) で来たときだけ、そのイベントのカードは開いておく
  // (そのイベントのために来ている。中の企画は閉じたまま)。
  // #2490 `&open=program` で来たときは、名指しした企画の枠も開いておく。スキャン直後の
  // 「合言葉を入れる」から来た人に、閉じた枠を探して開かせない
  const openFocusedProgram = searchParams.get('open') === 'program'
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  const isOpen = (key: string) =>
    opened[key] ??
    (!!focusEventId &&
      (!key.includes(':program:') || (openFocusedProgram && key.endsWith(`:program:${focusEventId}`))))
  const toggle = (key: string) => setOpened((m) => ({ ...m, [key]: !isOpen(key) }))

  // フルリロード直後は Firebase auth 復元前で auth.currentUser が null のため、
  // 最初の /stamp/context が匿名になり進捗が 0/3 に見える。復元完了を tick で
  // 検知して再取得する (SPA 内遷移では即座に 1 になり実質1回だけ走る)。
  const [authTick, setAuthTick] = useState(0)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setAuthTick((n) => n + 1)
    })
    return unsubscribe
  }, [])

  // 位置が確定したら (GPS/デモ偽装/手動地域) context を取得
  useEffect(() => {
    if (coords) {
      void loadContext({ lat: coords.lat, lng: coords.lng }, focusEventId ?? undefined)
    } else if (manualRegionId || focusEventId) {
      // #1869 リハーサルは名指しで開くので、位置も手動地域も無くても取りに行く。
      void loadContext(undefined, focusEventId ?? undefined)
    }
  }, [coords, manualRegionId, focusEventId, loadContext, authTick])

  // 戻ってきた時に進捗を最新化（StampGate / 外部アクティビティ経由の獲得を反映）
  useEffect(() => {
    if (context?.available && isLoggedIn) {
      void refreshProgress()
      void refreshActivityProgress()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const upcomingEvents = context?.upcomingEvents ?? []
  const endedEvents = context?.endedEvents ?? []

  // #1869 イベントを名指しで開いているときは位置が要らない (リハーサルは会場に
  // 居ない人が試す)。ここを直さないと、**読み込めた中身の上に位置情報の催促が
  // 出て**、しかもローディングとエラー表示がその裏で抑止される。
  const needsLocation = !coords && !manualRegionId && !focusEventId
  /**
   * 指定があればそのイベントだけに絞る (親の ID でも、中の企画の ID でもよい)。
   *
   * **企画の ID として一致するなら、そちらを優先する。** 移行で包んだ親は企画と同じ ID
   * なので、企画を別のイベントへ分けた後も元の親が同じ ID で残る。親の ID を先に見ると、
   * 配布済みの QR (企画の ID) から来た人に、分けた後の**別のイベント**を見せてしまう。
   * 開催中と開催予定の両方を見て決める — 片方のリストだけで判定すると、企画が予定側に
   * いるときに開催中側で親の ID に一致してしまう。
   */
  const focusIsProgram =
    !!focusEventId &&
    [...(context?.events ?? []), ...upcomingEvents, ...endedEvents].some((b) =>
      b.programs.some((p) => p.event.id === focusEventId),
    )
  const focused = <T extends { event: { id: string }; programs: { event: { id: string } }[] }>(
    blocks: T[],
  ): T[] => {
    if (!focusEventId) return blocks
    return focusIsProgram
      ? blocks.filter((b) => b.programs.some((p) => p.event.id === focusEventId))
      : blocks.filter((b) => b.event.id === focusEventId)
  }

  // タブ。**最初は件数のある最初のタブを開く** — 開催中が 0 件の時期 (告知の期間) に
  // 「開催中のイベントはありません」へ着地させない。入口 QR (`?event=`) で来たときも、
  // 絞った後の件数で決めるので、そのイベントのあるタブが開く。
  const tabBlocks = {
    open: context?.available ? focused(context.events) : [],
    upcoming: focused(upcomingEvents),
    ended: focused(endedEvents),
  }
  const [pickedTab, setPickedTab] = useState<EventTab | null>(null)
  const tab: EventTab = pickedTab ?? EVENT_TABS.find((k) => tabBlocks[k].length > 0) ?? 'open'
  const hasAnyEvent =
    (context?.available && context.events.length > 0) ||
    upcomingEvents.length > 0 ||
    endedEvents.length > 0
  // #1924 いま見せているものにリハーサル中のイベントが混じっているか。
  // 公開済みのイベントは rehearsalOpen が false になる (is_rehearsal_open は
  // isEnabled=False のときだけ true) ので、公開後は勝手に消える。
  const showingRehearsal = tabBlocks.open.some((b) => b.event.rehearsalOpen)

  // #2492 画面全体に出す PR 枠 (ポップアップ / 下部に固定)。イベントのカードの中の枠は、
  // カードを開かないと見えないため。開催中のイベントを先に、無ければ開催予定 (告知の期間
  // こそ出したい)。終了のイベントには出さない (読むだけのタブ)。
  //
  // ポップアップは**取り直しの間も出したまま**にする (context は取り直しの間も前の値が残る)。
  // 読み込み中は外すと、ログインの復元で取り直した瞬間に出たばかりのポップアップが消え、
  // その日はもう出なかった (出したことは記録済みのため)。
  const popupBlock = context
    ? [...tabBlocks.open, ...tabBlocks.upcoming].find((b) => b.slots.some((s) => s.placement === 'popup'))
    : undefined
  const bottomSlots = !!context && !contextLoading && tab !== 'ended'
    ? tabBlocks[tab]
        .flatMap((b) => b.slots.filter((s) => s.placement === 'bottom'))
        .filter((s, i, all) => all.findIndex((x) => x.id === s.id) === i)
    : []

  return (
    <div className={`min-h-screen bg-warm-white ${bottomSlots.length ? '' : 'pb-10'}`}>
      <Header title={t('stampRally.title')} onBack={goBack} />

      {/* ---- 位置未確定: 現在地 or 地域選択を促す ---- */}
      {needsLocation && (
        <div className="flex flex-col items-center px-8 py-16 text-center">
          <MapPin size={40} className="text-yokoso-red mb-3" />
          <p className="text-sm font-body text-text-secondary mb-6">
            {status === 'denied' || status === 'unavailable'
              ? t('stampRally.locationDenied')
              : t('stampRally.locationHint')}
          </p>
          {status !== 'denied' && status !== 'unavailable' && (
            <button
              onClick={requestLocation}
              disabled={status === 'loading'}
              className="w-full rounded-full bg-yokoso-red text-white py-3 text-sm font-body font-medium tap-effect disabled:opacity-60 mb-3"
            >
              {t('stampRally.useLocation')}
            </button>
          )}
          <button
            onClick={() => setPickerOpen(true)}
            className="w-full rounded-full border border-navy text-navy py-3 text-sm font-body font-medium tap-effect"
          >
            {t('stampRally.selectRegion')}
          </button>
        </div>
      )}

      {/* ---- ローディング / エラー ---- */}
      {!needsLocation && (contextLoading || (!context && !contextError)) && (
        <div className="flex justify-center py-20">
          <div className="w-7 h-7 border-2 border-navy border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!needsLocation && contextError && !contextLoading && (
        <p className="px-8 py-16 text-center text-sm font-body text-text-secondary">
          {t('stampRally.scanError')}
        </p>
      )}

      {/* ---- 圏外 / イベントなし ---- */}
      {context && !context.available && !contextLoading && context.reason !== 'upcoming' &&
        upcomingEvents.length === 0 && endedEvents.length === 0 && (
        <div className="flex flex-col items-center px-8 py-16 text-center">
          <MapPin size={40} className="text-text-tertiary mb-3" />
          <p className="font-body font-medium text-text-primary mb-1">
            {context.reason === 'no_region' ? t('stampRally.outOfArea') : t('stampRally.noEvent')}
          </p>
          <p className="text-xs font-body text-text-tertiary mb-6">
            {context.reason === 'no_region' ? t('stampRally.outOfAreaHint') : t('stampRally.noEventHint')}
          </p>
          {context.reason === 'no_region' && (
            <button
              onClick={() => setPickerOpen(true)}
              className="rounded-full border border-navy text-navy px-6 py-2.5 text-sm font-body font-medium tap-effect"
            >
              {t('stampRally.selectRegion')}
            </button>
          )}
        </div>
      )}

      {/* ---- リハーサル表示中の断り書き (#1924) ---- */}
      {context && !contextLoading && showingRehearsal && <RehearsalNotice />}

      {/* ---- タブ: 開催中 / 開催予定 / 終了 ---- */}
      {context && !contextLoading && hasAnyEvent && (
        <div role="tablist" className="mx-4 mt-4 flex gap-1 rounded-full bg-white p-1 shadow-card">
          {EVENT_TABS.map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setPickedTab(key)}
              className={`flex flex-1 items-center justify-center gap-1 rounded-full py-2 font-body text-sm font-medium transition-colors ${
                tab === key ? 'bg-navy text-white' : 'text-text-secondary'
              }`}
            >
              {t(TAB_LABEL[key])}
              <span className={`text-xs ${tab === key ? 'text-white/70' : 'text-text-tertiary'}`}>
                {tabBlocks[key].length}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* 選んだタブにイベントが無いとき */}
      {context && !contextLoading && hasAnyEvent && tabBlocks[tab].length === 0 && (
        <p className="px-8 py-12 text-center font-body text-sm text-text-secondary">
          {t(TAB_EMPTY[tab], { days: String(ENDED_LOOKBACK_DAYS) })}
        </p>
      )}

      {/* ---- 開催中: イベントごとのカード。中に企画が並ぶ (#1898) ---- */}
      {tab === 'open' && context?.available && !contextLoading && (
        <div className="flex flex-col gap-4 px-4 mt-4 animate-fade-in">
          {tabBlocks.open.map((block) => (
            <EventCard
              key={block.event.id}
              group={block.event}
              // #1924 リハーサル中は「開催中」と名乗らない。会期前でも開催中の枠に
              // 入るのは仕様だが、そのまま出すと本物と見分けが付かない
              badge={
                block.event.rehearsalOpen ? (
                  <>
                    <FlaskConical size={14} />
                    {t('stampRally.rehearsalBadge')}
                  </>
                ) : (
                  <>
                    <Stamp size={14} />
                    {t('stampRally.tabOpen')}
                  </>
                )
              }
              rehearsal={block.event.rehearsalOpen}
              // 同じイベントが開催中と開催予定の両方に出ることがある (企画ごとに会期が
              // 違う)。キーを分けないと、片方を畳むともう片方も畳まれる
              open={isOpen(`open:${block.event.id}`)}
              onToggle={() => toggle(`open:${block.event.id}`)}
            >
              {/* #1898 イベント枠はイベント (親) に付く。バナーはフェスの看板で、スタンプ
                  ラリーの企画だけのものではないので、企画より上に置く。PR 枠 (list) は企画の下 */}
              <SlotCarousel slots={block.slots.filter((s) => s.placement === 'top')} track={!block.event.rehearsalOpen} />
              {block.programs.map((program) => {
                // 企画ごとの開閉 (既定は閉じる)
                const key = `open:program:${program.event.id}`
                const open = isOpen(key)
                const onToggle = () => toggle(key)
                return program.kind === 'qr' ? (
                  <QrProgramSection
                    key={program.event.id}
                    program={program}
                    group={block.event}
                    isGuest={isGuest}
                    isLoggedIn={isLoggedIn}
                    focusEventId={focusEventId}
                    open={open}
                    onToggle={onToggle}
                  />
                ) : (
                  // 外部アクティビティ (#1154/#1159)。起動URLが解決できない企画は
                  // ActivityCard 自身が非表示にする
                  <ActivityCard
                    key={program.event.id}
                    arEvent={program}
                    group={block.event}
                    open={open}
                    onToggle={onToggle}
                  />
                )
              })}
              <SlotCarousel slots={block.slots.filter((s) => s.placement === 'list')} track={!block.event.rehearsalOpen} />
              <EventInfoLink eventId={block.event.id} />
            </EventCard>
          ))}
        </div>
      )}

      {/* ---- 開催予定 ----
          告知は開催のひと月前から出るので、開催前に来た人が一番多い時期がある。
          「開催中のイベントはありません」で終わらせず、何のイベントかを見せる。

          #1886 **開催中のイベントがあっても出す。** 以前は開催中が 1 つも無いときだけ
          出していたため、同じ地域で別のラリーが動いていると予定が TOP から消え、
          告知を見て来た人が着地できなかった。運営にとっても、公開したイベントを
          **当日まで画面で確認する手段が無くなる** (リハーサル受付は未公開の間だけの
          機能で、公開した瞬間に使えなくなる)。

          タブで分けてからは「開催予定」タブに出る。開催中が無い時期は最初からこのタブが開く。 */}
      {tab === 'upcoming' && context && !contextLoading && (
        <div className="flex flex-col gap-4 px-4 mt-4 animate-fade-in">
          {tabBlocks.upcoming.map((block) => (
            <EventCard
              key={block.event.id}
              group={block.event}
              badge={
                <>
                  <Stamp size={14} />
                  {t('stampRally.upcomingBadge')}
                </>
              }
              open={isOpen(`upcoming:${block.event.id}`)}
              onToggle={() => toggle(`upcoming:${block.event.id}`)}
            >
              {/* 掲載料をいただく PR 枠は開催前こそ出したい — 告知の期間がいちばん長く見られる */}
              <SlotCarousel slots={block.slots.filter((s) => s.placement === 'top')} track={!block.event.rehearsalOpen} />
              {block.programs.map((program) => {
                const key = `upcoming:program:${program.event.id}`
                return (
                  <UpcomingProgramSection
                    key={program.event.id}
                    program={program}
                    group={block.event}
                    open={isOpen(key)}
                    onToggle={() => toggle(key)}
                  />
                )
              })}
              <SlotCarousel slots={block.slots.filter((s) => s.placement === 'list')} track={!block.event.rehearsalOpen} />
              <EventInfoLink eventId={block.event.id} />
            </EventCard>
          ))}
        </div>
      )}

      {/* ---- 終了 ----
          終わってから ENDED_LOOKBACK_DAYS 日以内のイベント。参加した人が見返したり、次の出展者に
          「こういうイベントをやった」と見せたりする用。**読むだけ** — スタンプを押す操作や
          参加方法は出さず、企画の名前とイベント詳細への入口だけにする。 */}
      {tab === 'ended' && context && !contextLoading && tabBlocks.ended.length > 0 && (
        <div className="flex flex-col gap-4 px-4 mt-4 animate-fade-in">
          {tabBlocks.ended.map((block) => (
            <EventCard
              key={block.event.id}
              group={block.event}
              badge={
                <>
                  <Stamp size={14} />
                  {t('stampRally.tabEnded')}
                </>
              }
              open={isOpen(`ended:${block.event.id}`)}
              onToggle={() => toggle(`ended:${block.event.id}`)}
            >
              <p className="text-center font-body text-xs text-text-tertiary">
                {t('stampRally.endedNote')}
              </p>
              {block.programs.map((program) => (
                <div key={program.event.id} className="rounded-2xl bg-white px-4 py-3 shadow-card">
                  <ProgramTitle program={program} />
                </div>
              ))}
              <EventInfoLink eventId={block.event.id} />
            </EventCard>
          ))}
          <p className="text-center font-body text-xs text-text-tertiary">
            {t('stampRally.endedLookback', { days: String(ENDED_LOOKBACK_DAYS) })}
          </p>
        </div>
      )}

      {/* #2492 画面下に固定の PR 枠。タブを切り替えたら最初の枠から */}
      {bottomSlots.length > 0 && (
        // リハーサル中 (運営の確認) は数えない
        <SlotBottomBar key={tab} slots={bottomSlots} track={!showingRehearsal} />
      )}
      {popupBlock && (
        <SlotPopup
          key={popupBlock.event.id}
          groupId={popupBlock.event.id}
          slots={popupBlock.slots.filter((s) => s.placement === 'popup')}
          rehearsal={!!popupBlock.event.rehearsalOpen}
        />
      )}

      <StampRegionPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(regionId) => void setManualRegion(regionId)}
      />
    </div>
  )
}
